/**
 * Venues API module
 * Handles all venue-related API operations
 */

import { db, supabase } from '../lib/supabase';
import { createAPIMethod, createPaginatedAPIMethod } from './base';
import { log } from '../utils/logger';

/**
 * Venue API namespace
 */
export const venueAPI = {
  /**
   * Create a new venue
   * @param {Object} data - Venue data
   * @returns {Promise<APIResponse>}
   */
  create: createAPIMethod(
    (data) => db.createVenue(data),
    { operation: 'Create venue' }
  ),

  /**
   * Update an existing venue
   * @param {string} id - Venue ID
   * @param {Object} data - Updated venue data
   * @returns {Promise<APIResponse>}
   */
  update: createAPIMethod(
    (id, data) => db.updateVenue(id, data),
    { operation: 'Update venue' }
  ),

  /**
   * Delete a venue
   * @param {string} id - Venue ID
   * @returns {Promise<APIResponse>}
   */
  delete: createAPIMethod(
    async (id) => {
      return await supabase
        .from('venues')
        .delete()
        .eq('id', id);
    },
    { operation: 'Delete venue' }
  ),

  /**
   * Get a single venue by ID
   * @param {string} id - Venue ID
   * @returns {Promise<APIResponse>}
   */
  getById: createAPIMethod(
    async (id) => {
      return await supabase
        .from('venues')
        .select(`
          *,
          venue_cuisines:cuisine_id (
            id, category, subcategory, description
          ),
          venue_categories (
            id, name, description
          )
        `)
        .eq('id', id)
        .single();
    },
    { operation: 'Get venue' }
  ),

  /**
   * Get all venues
   * @param {Object} filters - Filter options
   * @returns {Promise<APIResponse>}
   */
  getAll: createAPIMethod(
    async (filters = {}) => {
      let query = supabase
        .from('venues')
        .select(`
          *,
          venue_cuisines:cuisine_id (
            id, category, subcategory, description
          ),
          venue_categories (
            id, name, description
          )
        `);

      // Apply filters
      if (filters.isActive !== undefined) {
        query = query.eq('is_active', filters.isActive);
      }
      // Note: Separate city filter removed - city is now included in main search
      if (filters.state) {
        query = query.eq('state', filters.state);
      }
      if (filters.categoryId) {
        query = query.eq('category_id', filters.categoryId);
      }
      if (filters.cuisineTypeId) {
        query = query.eq('cuisine_id', filters.cuisineTypeId);
      }
      if (filters.search) {
        // Enhanced search: includes name, description, city, and state
        query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%,city.ilike.%${filters.search}%,state.ilike.%${filters.search}%`);
      }

      // Geographic filtering - venues within radius of a point using Haversine formula
      if (filters.nearLocation && filters.radiusMiles) {
        const { latitude, longitude } = filters.nearLocation;
        const radiusMiles = filters.radiusMiles;
        
        log.info('🌍 Geographic search parameters:', {
          searchLat: latitude,
          searchLng: longitude,
          radiusMiles: radiusMiles
        });
        
        // Use simplified geographic bounding box first, then filter with JavaScript
        // This is more compatible with most SQL databases
        const milesToDegrees = radiusMiles / 69; // Rough conversion: 1 degree ≈ 69 miles
        const latMin = latitude - milesToDegrees;
        const latMax = latitude + milesToDegrees;
        const lonMin = longitude - milesToDegrees;
        const lonMax = longitude + milesToDegrees;
        
        log.info('🌍 Geographic bounding box:', {
          latMin, latMax, lonMin, lonMax,
          degreesRadius: milesToDegrees
        });
        
        query = query
          .gte('latitude', latMin)
          .lte('latitude', latMax)
          .gte('longitude', lonMin)
          .lte('longitude', lonMax);
      }

      // Enhanced geographic filtering - support for multiple location constraints
      // This allows filtering venues that must be within distance of BOTH game and hotel locations
      const hasGameLocationFilter = filters.gameLocation && filters.maxDistanceFromGame;
      const hasHotelLocationFilter = filters.hotelLocation && filters.maxDistanceFromHotel;
      
      if (hasGameLocationFilter || hasHotelLocationFilter) {
        log.info('🌍 Multiple location constraint filtering:', {
          hasGameLocationFilter,
          hasHotelLocationFilter,
          gameLocation: filters.gameLocation,
          maxDistanceFromGame: filters.maxDistanceFromGame,
          hotelLocation: filters.hotelLocation,
          maxDistanceFromHotel: filters.maxDistanceFromHotel
        });

        // Create a combined bounding box that includes both constraints
        // We'll need to use the intersection of both bounding boxes
        let combinedLatMin = -90, combinedLatMax = 90;
        let combinedLonMin = -180, combinedLonMax = 180;
        
        if (hasGameLocationFilter) {
          const { latitude, longitude } = filters.gameLocation;
          const radiusMiles = filters.maxDistanceFromGame;
          const milesToDegrees = radiusMiles / 69;
          
          const gameLatMin = latitude - milesToDegrees;
          const gameLatMax = latitude + milesToDegrees;
          const gameLonMin = longitude - milesToDegrees;
          const gameLonMax = longitude + milesToDegrees;
          
          combinedLatMin = Math.max(combinedLatMin, gameLatMin);
          combinedLatMax = Math.min(combinedLatMax, gameLatMax);
          combinedLonMin = Math.max(combinedLonMin, gameLonMin);
          combinedLonMax = Math.min(combinedLonMax, gameLonMax);
          
          log.info('🎮 Game location bounding box:', { gameLatMin, gameLatMax, gameLonMin, gameLonMax });
        }
        
        if (hasHotelLocationFilter) {
          const { latitude, longitude } = filters.hotelLocation;
          const radiusMiles = filters.maxDistanceFromHotel;
          const milesToDegrees = radiusMiles / 69;
          
          const hotelLatMin = latitude - milesToDegrees;
          const hotelLatMax = latitude + milesToDegrees;
          const hotelLonMin = longitude - milesToDegrees;
          const hotelLonMax = longitude + milesToDegrees;
          
          combinedLatMin = Math.max(combinedLatMin, hotelLatMin);
          combinedLatMax = Math.min(combinedLatMax, hotelLatMax);
          combinedLonMin = Math.max(combinedLonMin, hotelLonMin);
          combinedLonMax = Math.min(combinedLonMax, hotelLonMax);
          
          log.info('🏨 Hotel location bounding box:', { hotelLatMin, hotelLatMax, hotelLonMin, hotelLonMax });
        }
        
        log.info('🌍 Combined bounding box:', {
          combinedLatMin, combinedLatMax, combinedLonMin, combinedLonMax
        });
        
        // Apply the combined bounding box to the query
        query = query
          .gte('latitude', combinedLatMin)
          .lte('latitude', combinedLatMax)
          .gte('longitude', combinedLonMin)
          .lte('longitude', combinedLonMax);
      }

      // Add ordering
      query = query.order('name', { ascending: true });

      const result = await query;
      
      // Log geographic search results
      if (filters.nearLocation && filters.radiusMiles) {
        log.info('🌍 Geographic search results:', {
          totalFound: result.data?.length || 0,
          venues: result.data?.map(v => ({
            name: v.name,
            lat: v.latitude,
            lng: v.longitude,
            city: v.city
          })) || []
        });
        
        // Also check how many venues exist without geographic filter
        const allVenuesQuery = supabase.from('venues').select('id, name, latitude, longitude, city').eq('is_active', true);
        const allVenuesResult = await allVenuesQuery;
        log.info('🌍 All venues in database for comparison:', {
          totalVenues: allVenuesResult.data?.length || 0,
          venues: allVenuesResult.data?.map(v => ({
            name: v.name,
            lat: v.latitude,
            lng: v.longitude,
            city: v.city
          })) || []
        });
      }

      return result;
    },
    { operation: 'Get venues' }
  ),

  /**
   * Get venues with pagination
   * @param {number} page - Page number
   * @param {Object} filters - Filter options
   * @returns {Promise<APIResponse>}
   */
  getPaginated: createPaginatedAPIMethod(
    async ({ from, to, ...filters }) => {
      let query = supabase
        .from('venues')
        .select(`
          *,
          venue_cuisines:cuisine_id (
            id, category, subcategory, description
          ),
          venue_categories (
            id, name, description
          )
        `, { count: 'exact' });

      // Apply filters
      if (filters.isActive !== undefined) {
        query = query.eq('is_active', filters.isActive);
      }
      if (filters.search) {
        query = query.or(`name.ilike.%${filters.search}%,description.ilike.%${filters.search}%`);
      }

      // Add pagination
      query = query.range(from, to);
      
      // Add ordering
      query = query.order('name', { ascending: true });

      return await query;
    },
    { operation: 'Get venues paginated', pageSize: 20 }
  ),

  /**
   * Search venues by location
   * @param {Object} location - Location parameters
   * @returns {Promise<APIResponse>}
   */
  searchByLocation: createAPIMethod(
    async ({ _latitude, _longitude, _radiusMiles = 10 }) => {
      // This would typically use PostGIS or similar for geo queries
      // For now, simplified implementation
      return await supabase
        .from('venues')
        .select('*')
        .eq('is_active', true)
        .order('name');
    },
    { operation: 'Search venues by location' }
  ),

  /**
   * Get venue availability (DEPRECATED - use checkCapacityAvailability)
   * @param {string} venueId - Venue ID
   * @param {string} date - Date to check (YYYY-MM-DD)
   * @param {string} time - Time to check (HH:MM)
   * @returns {Promise<APIResponse>}
   */
  checkAvailability: createAPIMethod(
    async (venueId, date, time) => {
      // Check if venue has any reservations at the specified time
      const datetime = `${date}T${time}:00`;

      const { data: existingReservations } = await supabase
        .from('reservations')
        .select('id')
        .eq('venue_id', venueId)
        .eq('reserved_at', datetime)
        .neq('status', 'cancelled');

      return {
        available: !existingReservations || existingReservations.length === 0,
        conflictCount: existingReservations?.length || 0
      };
    },
    { operation: 'Check venue availability' }
  ),

  /**
   * Check venue capacity availability (capacity-aware)
   * @param {string} venueId - Venue ID
   * @param {string} date - Date to check (YYYY-MM-DD)
   * @param {string} startTime - Start time (HH:MM)
   * @param {number} durationHours - Duration in hours (default: 2)
   * @param {number} partySize - Party size to accommodate
   * @param {string} excludeReservationId - Reservation ID to exclude (for updates)
   * @returns {Promise<APIResponse>}
   */
  checkCapacityAvailability: createAPIMethod(
    async (venueId, date, startTime, durationHours = 2, partySize = null, excludeReservationId = null) => {
      const { data, error } = await supabase.rpc('check_venue_capacity_at_time', {
        p_venue_id: venueId,
        p_date: date,
        p_start_time: startTime,
        p_duration_hours: durationHours,
        p_exclude_reservation_id: excludeReservationId
      });

      if (error) throw error;

      // Check if party size would fit in remaining capacity
      const canAccommodate = partySize === null || data.capacity_remaining >= partySize;

      return {
        ...data,
        can_accommodate: canAccommodate,
        party_size: partySize
      };
    },
    { operation: 'Check venue capacity availability' }
  ),

  /**
   * Get venue statistics
   * @param {string} venueId - Venue ID
   * @returns {Promise<APIResponse>}
   */
  getStatistics: createAPIMethod(
    async (venueId) => {
      // Get reservation counts by status
      const { data: reservations } = await supabase
        .from('reservations')
        .select('status')
        .eq('venue_id', venueId);

      const stats = {
        total: reservations?.length || 0,
        pending: 0,
        confirmed: 0,
        completed: 0,
        cancelled: 0
      };

      reservations?.forEach(r => {
        if (stats[r.status] !== undefined) {
          stats[r.status]++;
        }
      });

      return stats;
    },
    { operation: 'Get venue statistics' }
  ),

  /**
   * Update venue availability settings
   * @param {string} venueId - Venue ID
   * @param {Object} availability - Availability settings
   * @returns {Promise<APIResponse>}
   */
  updateAvailability: createAPIMethod(
    async (venueId, availability) => {
      return await supabase
        .from('venue_availability')
        .upsert({
          venue_id: venueId,
          ...availability,
          updated_at: new Date().toISOString()
        })
        .select()
        .single();
    },
    { operation: 'Update venue availability' }
  ),

  /**
   * Get venue categories
   * @returns {Promise<APIResponse>}
   */
  getCategories: createAPIMethod(
    async () => {
      return await supabase
        .from('venue_categories')
        .select('*')
        .order('name');
    },
    { operation: 'Get venue categories' }
  ),

  /**
   * Get cuisine types
   * @returns {Promise<APIResponse>}
   */
  getCuisineTypes: createAPIMethod(
    async () => {
      return await supabase
        .from('venue_cuisines')
        .select('*')
        .eq('is_active', true)
        .order('category, subcategory');
    },
    { operation: 'Get cuisine types' }
  ),

  /**
   * Get venues for a specific manager
   * @param {string} managerId - Manager user ID
   * @returns {Promise<APIResponse>}
   */
  getByManager: createAPIMethod(
    async (managerId) => {
      return await supabase
        .from('venues')
        .select(`
          *,
          venue_cuisines:cuisine_id (
            id, category, subcategory, description
          ),
          venue_categories (
            id, name, description
          )
        `)
        .eq('manager_id', managerId)
        .order('name');
    },
    { operation: 'Get manager venues' }
  ),

  /**
   * Toggle venue active status
   * @param {string} id - Venue ID
   * @returns {Promise<APIResponse>}
   */
  toggleActive: createAPIMethod(
    async (id) => {
      // First get current status
      const { data: venue } = await supabase
        .from('venues')
        .select('is_active')
        .eq('id', id)
        .single();

      if (!venue) {
        throw new Error('Venue not found');
      }

      // Toggle the status
      return await supabase
        .from('venues')
        .update({ 
          is_active: !venue.is_active,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
    },
    { operation: 'Toggle venue active status' }
  ),

  /**
   * Get backup venue suggestions
   * @param {Object} criteria - Search criteria
   * @returns {Promise<APIResponse>}
   */
  getBackupSuggestions: createAPIMethod(
    async ({ venueId, categoryId, _cuisineTypeId, city, excludeIds = [] }) => {
      let query = supabase
        .from('venues')
        .select(`
          *,
          venue_cuisines:cuisine_id (
            id, category, subcategory, description
          )
        `)
        .eq('is_active', true)
        .neq('id', venueId);

      // Exclude already selected venues
      if (excludeIds.length > 0) {
        query = query.not('id', 'in', `(${excludeIds.join(',')})`);
      }

      // Prioritize same category
      if (categoryId) {
        query = query.eq('category_id', categoryId);
      }

      // Prioritize same city
      if (city) {
        query = query.eq('city', city);
      }

      // Limit results
      query = query.limit(10);

      return await query;
    },
    { operation: 'Get backup venue suggestions' }
  ),

  /**
   * Get all game locations
   * @returns {Promise<APIResponse>}
   */
  getGameLocations: createAPIMethod(
    async () => {
      return await supabase
        .from('game_locations')
        .select('*')
        .eq('is_active', true)
        .order('name');
    },
    { operation: 'Get game locations' }
  ),

  /**
   * Get game location by ID
   * @param {string} id - Game location ID
   * @returns {Promise<APIResponse>}
   */
  getGameLocationById: createAPIMethod(
    async (id) => {
      return await supabase
        .from('game_locations')
        .select('*')
        .eq('id', id)
        .single();
    },
    { operation: 'Get game location' }
  ),

  /**
   * Create a new game location
   * @param {Object} data - Game location data
   * @returns {Promise<APIResponse>}
   */
  createGameLocation: createAPIMethod(
    async (data) => {
      return await supabase
        .from('game_locations')
        .insert([data])
        .select()
        .single();
    },
    { operation: 'Create game location' }
  ),

  /**
   * Update an existing game location
   * @param {string} id - Game location ID
   * @param {Object} data - Updated game location data
   * @returns {Promise<APIResponse>}
   */
  updateGameLocation: createAPIMethod(
    async (id, data) => {
      return await supabase
        .from('game_locations')
        .update({
          ...data,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
    },
    { operation: 'Update game location' }
  ),

  /**
   * Delete a game location
   * @param {string} id - Game location ID
   * @returns {Promise<APIResponse>}
   */
  deleteGameLocation: createAPIMethod(
    async (id) => {
      return await supabase
        .from('game_locations')
        .delete()
        .eq('id', id);
    },
    { operation: 'Delete game location' }
  ),

  /**
   * Toggle game location active status
   * @param {string} id - Game location ID
   * @returns {Promise<APIResponse>}
   */
  toggleGameLocationActive: createAPIMethod(
    async (id) => {
      // First get current status
      const { data: location } = await supabase
        .from('game_locations')
        .select('is_active')
        .eq('id', id)
        .single();

      if (!location) {
        throw new Error('Game location not found');
      }

      // Toggle the status
      return await supabase
        .from('game_locations')
        .update({
          is_active: !location.is_active,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
    },
    { operation: 'Toggle game location active status' }
  ),

  /**
   * Upload a photo for a venue
   * @param {string} venueId - Venue ID
   * @param {File} file - Image file to upload
   * @param {Object} options - Upload options (caption, isPrimary)
   * @returns {Promise<APIResponse>}
   */
  uploadPhoto: createAPIMethod(
    async (venueId, file, options = {}) => {
      const { caption = '', isPrimary = false } = options;

      // Validate file
      if (!file) {
        throw new Error('No file provided');
      }

      // Validate file size (5MB max)
      const maxSizeBytes = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSizeBytes) {
        throw new Error('File size exceeds 5MB limit');
      }

      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid file type. Allowed: JPG, PNG, WebP');
      }

      // Generate unique file path
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const fileName = `${timestamp}_${Math.random().toString(36).substring(7)}.${fileExt}`;
      const storagePath = `${venueId}/${fileName}`;

      // Upload to Supabase Storage
      const { data: _uploadData, error: uploadError } = await supabase.storage
        .from('venue-photos')
        .upload(storagePath, file, {
          contentType: file.type,
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('venue-photos')
        .getPublicUrl(storagePath);

      // Get current venue data
      const { data: venue } = await supabase
        .from('venues')
        .select('photos, primary_photo_url, primary_photo_storage_path')
        .eq('id', venueId)
        .single();

      if (!venue) {
        // Clean up uploaded file if venue not found
        await supabase.storage.from('venue-photos').remove([storagePath]);
        throw new Error('Venue not found');
      }

      log.info('📸 Upload photo - venue data before upload:', {
        venueId,
        hasPhotosArray: !!venue.photos,
        photosCount: venue.photos?.length || 0,
        hasPrimaryPhotoUrl: !!venue.primary_photo_url,
        primaryPhotoUrl: venue.primary_photo_url
      });

      // Create photo metadata
      const photoMetadata = {
        url: publicUrl,
        storage_path: storagePath,
        is_primary: isPrimary,
        caption: caption || '',
        uploaded_at: new Date().toISOString(),
        uploaded_by: (await supabase.auth.getUser()).data.user?.id || null
      };

      // Update photos array
      const currentPhotos = venue.photos || [];
      const isFirstPhoto = currentPhotos.length === 0;

      log.info('📸 Upload photo - current photos array length:', currentPhotos.length);
      log.info('📸 Upload photo - isPrimary option from client:', isPrimary);
      log.info('📸 Upload photo - isFirstPhoto:', isFirstPhoto);

      // Auto-set as primary if this is the first photo, regardless of client flag
      const shouldBePrimary = isFirstPhoto || isPrimary;

      // If this is set as primary, unmark all other photos
      const updatedPhotos = shouldBePrimary
        ? currentPhotos.map(p => ({ ...p, is_primary: false })).concat(photoMetadata)
        : currentPhotos.concat(photoMetadata);

      // Update the is_primary flag in photoMetadata to reflect the decision
      if (shouldBePrimary) {
        photoMetadata.is_primary = true;
        // Update the last item in updatedPhotos (the one we just added)
        updatedPhotos[updatedPhotos.length - 1].is_primary = true;
      }

      // Update venue record
      const updateData = {
        photos: updatedPhotos,
        updated_at: new Date().toISOString()
      };

      // If this is primary or first photo, update primary photo fields
      log.info('📸 Upload photo - should set as primary (final decision):', shouldBePrimary);

      if (shouldBePrimary) {
        updateData.primary_photo_url = publicUrl;
        updateData.primary_photo_storage_path = storagePath;
        log.info('📸 Upload photo - setting primary_photo_url:', publicUrl);
      }

      log.info('📸 Upload photo - updateData being sent to database:', {
        hasPhotos: !!updateData.photos,
        photosCount: updateData.photos?.length,
        hasPrimaryPhotoUrl: !!updateData.primary_photo_url,
        primaryPhotoUrl: updateData.primary_photo_url
      });

      const { data: updatedVenue, error: updateError } = await supabase
        .from('venues')
        .update(updateData)
        .eq('id', venueId)
        .select()
        .single();

      log.info('📸 Upload photo - database update result:', {
        success: !updateError,
        error: updateError,
        updatedVenue: updatedVenue ? {
          id: updatedVenue.id,
          photosCount: updatedVenue.photos?.length,
          hasPrimaryPhotoUrl: !!updatedVenue.primary_photo_url,
          primaryPhotoUrl: updatedVenue.primary_photo_url
        } : null
      });

      if (updateError) {
        // Clean up uploaded file if database update fails
        await supabase.storage.from('venue-photos').remove([storagePath]);
        throw updateError;
      }

      return updatedVenue;
    },
    { operation: 'Upload venue photo' }
  ),

  /**
   * Delete a photo from a venue
   * @param {string} venueId - Venue ID
   * @param {string} storagePath - Storage path of the photo to delete
   * @returns {Promise<APIResponse>}
   */
  deletePhoto: createAPIMethod(
    async (venueId, storagePath) => {
      // Get current venue data
      const { data: venue } = await supabase
        .from('venues')
        .select('photos, primary_photo_url, primary_photo_storage_path')
        .eq('id', venueId)
        .single();

      if (!venue) {
        throw new Error('Venue not found');
      }

      // Find and remove the photo from array
      const currentPhotos = venue.photos || [];
      const photoToDelete = currentPhotos.find(p => p.storage_path === storagePath);

      if (!photoToDelete) {
        throw new Error('Photo not found');
      }

      const updatedPhotos = currentPhotos.filter(p => p.storage_path !== storagePath);

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('venue-photos')
        .remove([storagePath]);

      if (storageError) {
        log.error('Failed to delete photo from storage:', storageError);
        // Continue with database update even if storage delete fails
      }

      // Update venue record
      const updateData = {
        photos: updatedPhotos,
        updated_at: new Date().toISOString()
      };

      // If this was the primary photo, update primary photo fields
      if (venue.primary_photo_storage_path === storagePath) {
        // Set first remaining photo as primary, or null if no photos left
        const newPrimaryPhoto = updatedPhotos.find(p => p.is_primary) || updatedPhotos[0];

        if (newPrimaryPhoto) {
          updateData.primary_photo_url = newPrimaryPhoto.url;
          updateData.primary_photo_storage_path = newPrimaryPhoto.storage_path;
          // Mark it as primary in the array
          updateData.photos = updatedPhotos.map(p => ({
            ...p,
            is_primary: p.storage_path === newPrimaryPhoto.storage_path
          }));
        } else {
          updateData.primary_photo_url = null;
          updateData.primary_photo_storage_path = null;
        }
      }

      return await supabase
        .from('venues')
        .update(updateData)
        .eq('id', venueId)
        .select()
        .single();
    },
    { operation: 'Delete venue photo' }
  ),

  /**
   * Set a photo as the primary photo for a venue
   * @param {string} venueId - Venue ID
   * @param {string} storagePath - Storage path of the photo to set as primary
   * @returns {Promise<APIResponse>}
   */
  setPrimaryPhoto: createAPIMethod(
    async (venueId, storagePath) => {
      // Get current venue data
      const { data: venue } = await supabase
        .from('venues')
        .select('photos')
        .eq('id', venueId)
        .single();

      if (!venue) {
        throw new Error('Venue not found');
      }

      const currentPhotos = venue.photos || [];
      const photoToSetPrimary = currentPhotos.find(p => p.storage_path === storagePath);

      if (!photoToSetPrimary) {
        throw new Error('Photo not found');
      }

      // Update all photos to mark only the selected one as primary
      const updatedPhotos = currentPhotos.map(p => ({
        ...p,
        is_primary: p.storage_path === storagePath
      }));

      // Update venue record
      return await supabase
        .from('venues')
        .update({
          photos: updatedPhotos,
          primary_photo_url: photoToSetPrimary.url,
          primary_photo_storage_path: storagePath,
          updated_at: new Date().toISOString()
        })
        .eq('id', venueId)
        .select()
        .single();
    },
    { operation: 'Set primary venue photo' }
  ),

  /**
   * Update photo caption
   * @param {string} venueId - Venue ID
   * @param {string} storagePath - Storage path of the photo
   * @param {string} caption - New caption
   * @returns {Promise<APIResponse>}
   */
  updatePhotoCaption: createAPIMethod(
    async (venueId, storagePath, caption) => {
      // Get current venue data
      const { data: venue } = await supabase
        .from('venues')
        .select('photos')
        .eq('id', venueId)
        .single();

      if (!venue) {
        throw new Error('Venue not found');
      }

      const currentPhotos = venue.photos || [];
      const photoIndex = currentPhotos.findIndex(p => p.storage_path === storagePath);

      if (photoIndex === -1) {
        throw new Error('Photo not found');
      }

      // Update the caption
      const updatedPhotos = [...currentPhotos];
      updatedPhotos[photoIndex] = {
        ...updatedPhotos[photoIndex],
        caption: caption || ''
      };

      // Update venue record
      return await supabase
        .from('venues')
        .update({
          photos: updatedPhotos,
          updated_at: new Date().toISOString()
        })
        .eq('id', venueId)
        .select()
        .single();
    },
    { operation: 'Update venue photo caption' }
  )
};